// src/pages/gro/ActionPlanModelsPage.tsx
import { useMemo, useState } from "react";
import { CheckCircle2, Eye, Pencil, Plus, Search, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useActionPlanModels, useSetActionPlanModelStatus } from "@/hooks/useActionPlanModels";
import { PERMISSIONS } from "@/lib/permissions";

function statusBadge(isActive: boolean) {
  return isActive ? <Badge>Ativo</Badge> : <Badge variant="outline">Inativo</Badge>;
}

export default function ActionPlanModelsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.permissions?.includes(PERMISSIONS.ACTION_PLANS_WRITE) ?? false;

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const modelsQ = useActionPlanModels({
    q,
    is_active: statusFilter === "all" ? undefined : statusFilter === "active",
  });
  const setStatusMut = useSetActionPlanModelStatus();

  const items = modelsQ.data ?? [];
  const counts = useMemo(
    () => ({
      total: items.length,
      active: items.filter((item) => item.is_active).length,
      inactive: items.filter((item) => !item.is_active).length,
    }),
    [items],
  );

  async function toggleStatus(id: string, next: boolean) {
    await setStatusMut.mutateAsync({ modelId: id, is_active: next });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Planos de Ação"
        description="Cadastre planos reutilizáveis e suas ações para uso posterior nas regras e no inventário."
      >
        {canWrite ? (
          <Button onClick={() => navigate("/gro/action-plan-models/novo")}>
            <Plus className="mr-2 h-4 w-4" /> Novo plano
          </Button>
        ) : null}
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4"><div className="text-sm text-muted-foreground">Total</div><div className="text-2xl font-semibold">{counts.total}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Ativos</div><div className="text-2xl font-semibold">{counts.active}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Inativos</div><div className="text-2xl font-semibold">{counts.inactive}</div></Card>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_220px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              placeholder="Buscar por código, título ou descrição"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="inactive">Inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {items.length === 0 ? (
        <EmptyState
          title="Nenhum plano cadastrado"
          description="Cadastre o primeiro plano de ação reutilizável."
          actionLabel={canWrite ? "Cadastrar primeiro plano" : undefined}
          onAction={canWrite ? () => navigate("/gro/action-plan-models/novo") : undefined}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Qtd. ações</TableHead>
                <TableHead className="text-right">Ações rápidas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.code}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{statusBadge(item.is_active)}</TableCell>
                  <TableCell>{item.items_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <PermissionIconButton
                        permission={PERMISSIONS.ACTION_PLANS_READ}
                        onClick={() => navigate(`/gro/action-plan-models/${item.id}`)}
                        label="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        permission={PERMISSIONS.ACTION_PLANS_WRITE}
                        onClick={() => navigate(`/gro/action-plan-models/${item.id}/editar`)}
                        label="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </PermissionIconButton>

                      {item.is_active ? (
                        <PermissionIconButton
                          permission={PERMISSIONS.ACTION_PLANS_WRITE}
                          onClick={() => toggleStatus(item.id, false)}
                          label="Inativar"
                        >
                          <XCircle className="h-4 w-4" />
                        </PermissionIconButton>
                      ) : (
                        <PermissionIconButton
                          permission={PERMISSIONS.ACTION_PLANS_WRITE}
                          onClick={() => toggleStatus(item.id, true)}
                          label="Ativar"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </PermissionIconButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
